import { Component, OnInit, signal, ChangeDetectionStrategy, inject, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';

import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { SpacesService } from '@generated/services/spaces.service';
import { AuthService } from '@core/services/auth.service';
import { SpaceDto } from '@generated/models';

@Component({
  selector: 'app-spaces',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    MenuModule,
    TooltipModule
],
  templateUrl: './spaces.html',
  styleUrl: './spaces.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpacesComponent implements OnInit {
  spaces = signal<SpaceDto[]>([]);
  filteredSpaces = signal<SpaceDto[]>([]);
  searchTerm = signal('');
  loading = signal(true);

  private spacesService = inject(SpacesService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private myUserId = this.authService.getUserProfile()?.id;

  constructor(private router: Router) {}

  ngOnInit() {
    this.loadSpaces();
  }

  loadSpaces() {
    this.loading.set(true);
    this.spacesService.apiSpacesGet().subscribe({
      next: (spaces) => {
        this.spaces.set(spaces);
        this.filteredSpaces.set(spaces);
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading spaces:', error);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onSearch() {
    const term = this.searchTerm().toLowerCase();
    const filtered = this.spaces().filter(space => 
      space.name?.toLowerCase().includes(term) ||
      space.owner_email?.toLowerCase().includes(term) ||
      space.user_role?.toLowerCase().includes(term)
    );
    this.filteredSpaces.set(filtered);
  }

  createNewSpace() {
    this.router.navigate(['/spaces/new']);
  }

  editSpace(space: SpaceDto) {
    this.router.navigate(['/spaces', space.id]);
  }

  showSpaceMenu(space: SpaceDto, event: Event) {
    // TODO: Show context menu for space actions
    console.log('Show menu for space:', space.name);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  isMyDefault(space: SpaceDto): boolean {
    return !!space.is_default && space.owner_id === this.myUserId;
  }

  getRoleDisplayName(role?: string): string {
    if (!role) return '';
    const roleMap: { [key: string]: string } = {
      'Owner': 'Owner',
      'Maintainer': 'Maintainer', 
      'Developer': 'Developer',
      'Reporter': 'Reporter',
      'Planner': 'Planner',
      'Guest': 'Guest'
    };
    return roleMap[role] || role;
  }
}